import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Plus } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMENSIONS = [
  ["Evidence Completeness", "25%", "Percentage of all market evidence requirements covered by existing assets as-is; requirements covered only via adaptation count at half credit. Computed by the Gap Engine over every requirement of every target market."],
  ["Country Coverage", "20%", "Percentage of target markets immediately supportable — i.e. assigned to Wave 1 (Submit Today) by the Submission Intelligence Engine."],
  ["Evidence Reusability", "20%", "Percentage of requirements served by shared, authored-once content (global-scope assets accepted as-is), measuring how far the portfolio travels without market-by-market rework."],
  ["Submission Readiness", "20%", "Average of the per-market submission readiness scores (0–100 each), which weight requirement coverage by requirement level and localization needs."],
  ["Localization Burden (inverted)", "15%", "Inverse of the average irreducibly-local annexure items demanded per market — fewer mandatory local items yields a higher score."],
  ["GVD Readiness (conditional)", "15% — joins once a dossier is assessed", "The latest Global Value Dossier assessment alignment score. Until a GVD is uploaded and assessed, this dimension is pending and the five live weights renormalize to 100%."],
];

export default function MethodologyPage() {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ version: "", change: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/methodology`).then((r) => setDoc(r.data)).catch(console.error);
  }, []);

  const addEntry = async () => {
    if (!form.version.trim() || !form.change.trim()) return toast.error("Version and change description are required");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/methodology/changelog`, form);
      setDoc(r.data);
      setForm({ version: "", change: "" });
      toast.success("Changelog entry added — methodology version updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add entry");
    } finally { setSaving(false); }
  };

  return (
    <PageShell pageId="platform" overline="Governance · Radical Score Transparency" title="GMARI™ Methodology Note">
      {!doc ? (
        <p className="py-24 text-center text-xs text-slate-400">Loading methodology…</p>
      ) : (
        <div className="max-w-4xl space-y-6">
          <div data-testid="methodology-version" className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center gap-8">
            <div><p className="overline-label text-white/50">Methodology Version</p><p className="font-heading font-extrabold text-2xl mt-1">v{doc.version}</p></div>
            <div><p className="overline-label text-white/50">Last Updated</p><p className="font-heading font-bold text-base mt-1">{doc.last_updated}</p></div>
            <p className="text-[11px] text-white/60 max-w-sm ml-auto">Versioned separately from the data — a score can change daily as evidence evolves; the methodology only changes when a row is added below.</p>
          </div>

          <section data-testid="methodology-formula" className="bg-white border border-slate-200 px-8 py-7 space-y-5">
            <div>
              <h2 className="font-heading font-bold text-lg tracking-tight">1. The GMARI™ Formula</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                GMARI™ is a deterministic weighted composite of six readiness dimensions, each scored 0–100 from the structured knowledge graph. No probabilistic AI is involved in the score itself.
              </p>
              <pre className="font-mono-data text-xs bg-slate-50 border border-slate-200 px-4 py-3 mt-3 whitespace-pre-wrap">
{`GMARI = Σ ( dimension_score × weight ) / Σ ( weight )

With a GVD assessed:
GMARI = 0.25·EC + 0.20·CC + 0.20·ER + 0.20·SR + 0.15·LB⁻¹ + 0.15·GVD  (weights renormalized to 1.0)

Without a GVD assessed:
GMARI = (0.25·EC + 0.20·CC + 0.20·ER + 0.20·SR + 0.15·LB⁻¹) / 1.00`}
              </pre>
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg tracking-tight">2. The Dimensions & Exact Weights</h2>
              <div className="mt-3 divide-y divide-slate-100 border border-slate-200">
                {DIMENSIONS.map(([name, weight, calc]) => (
                  <div key={name} className="px-4 py-3 flex gap-4">
                    <span className="font-heading font-extrabold text-sm text-slate-900 w-16 shrink-0">{weight}</span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{calc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg tracking-tight">3. Bands</h2>
              <p className="text-sm text-slate-600 mt-2">80–100 Launch Ready · 65–79 Advanced · 50–64 Developing · 0–49 Foundational.</p>
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg tracking-tight">4. Data vs Methodology</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                The score's "as of" date (shown next to every GMARI badge) reflects the most recent evidence or market data change. The methodology version above only changes when the formula or weights change — recorded in the changelog below.
              </p>
            </div>
          </section>

          <section data-testid="methodology-changelog" className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <p className="font-heading font-bold text-sm tracking-tight">Methodology Changelog</p>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-200 text-left">
                {["Version", "Date", "Change"].map((h) => <th key={h} className="px-5 py-2.5 overline-label text-slate-400 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {[...doc.changelog].reverse().map((c, i) => (
                  <tr key={i} data-testid={`changelog-row-${c.version}`}>
                    <td className="px-5 py-2.5 font-mono-data font-bold whitespace-nowrap align-top">v{c.version}</td>
                    <td className="px-5 py-2.5 font-mono-data whitespace-nowrap align-top">{c.date}</td>
                    <td className="px-5 py-2.5 text-slate-600 align-top">{c.change}{c.added_by && <span className="text-slate-400"> — {c.added_by}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {user?.role === "admin" && (
              <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-2">
                <Input data-testid="changelog-version-input" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="e.g. 1.1" className="h-8 w-24 text-xs bg-white" />
                <Input data-testid="changelog-change-input" value={form.change} onChange={(e) => setForm({ ...form, change: e.target.value })}
                  placeholder="e.g. Adjusted Localization Burden weight from 15% to 18%" className="h-8 flex-1 min-w-64 text-xs bg-white" />
                <button data-testid="changelog-add-btn" disabled={saving} onClick={addEntry}
                  className="bg-slate-900 text-white text-[11px] font-semibold px-3.5 py-2 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
                  <Plus size={11} weight="bold" /> Add changelog entry
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </PageShell>
  );
}
